import { getSupabaseClient } from '../../offline/supabase'
import type { AccumulationMode, WallHeadline } from '../display/preferences'
import type { ProjectionLevel, SharedProjection } from './contracts'

export type SharingStateRow = {
  id: string
  name: string
  youShare: ProjectionLevel | null
  sharedWithYou: ProjectionLevel | null
  incomingDisplay: ProjectionLevel | null
}

export type PendingInvitation = {
  id: string
  email: string
  projectionLevel: ProjectionLevel
  expiresAt: string
}

export type ActiveWall = {
  id: string
  projectionLevel: ProjectionLevel
  expiresAt: string | null
}

export type SharingState = {
  rows: SharingStateRow[]
  pendingInvitations: PendingInvitation[]
  wall: ActiveWall | null
}

export type ProjectedPerson = { projection: SharedProjection; stale: boolean }
export type WallDisplayPreferences = {
  wallHeadlines: readonly WallHeadline[]
  /** Accepted temporarily so existing deployed functions remain compatible during rollout. */
  wallHeadline?: WallHeadline
  wallAccumulation: AccumulationMode
  wallCategories: boolean
  wallSuccessfulItems: boolean
}
export type OwnerDisplaySettings = {
  displayVersion: number
  scoreboard: { showCategories: boolean; showSuccessfulItems: boolean; accumulation: AccumulationMode }
  wall: { showCategories: boolean; showSuccessfulItems: boolean; headlines: readonly WallHeadline[]; headline?: WallHeadline; accumulation: AccumulationMode }
}

export async function callSocialFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const client = getSupabaseClient()
  if (!client) throw new Error('Staging sharing is not configured in this build.')
  const { data, error } = await client.functions.invoke(name, { body })
  if (error || !data) throw new Error('The link is invalid or has expired.')
  return data as T
}

export const inspectInvitation = (token: string) => callSocialFunction<{
  valid: true
  inviterDisplayName: string
  projectionLevel: ProjectionLevel
  expiresAt: string
}>('invitation-inspect', { token })

export const actOnInvitation = (token: string, action: 'accept' | 'decline') =>
  callSocialFunction<{ state: 'accepted' | 'declined' }>('invitation-accept', { token, action })

export const inspectWall = (token: string) => callSocialFunction<{
  people: ProjectedPerson[]
  generatedAt: string
  display: WallDisplayPreferences
}>('wall-projection', { token })

export const loadSharedProjections = () => callSocialFunction<{
  people: ProjectedPerson[]
  generatedAt: string
}>('shared-projection', { selfLevel: 'items' })

export const loadSharingState = () => callSocialFunction<SharingState>('share-updates', {
  operation: 'load-state',
})

export const loadWallDisplayPreferences = () => callSocialFunction<WallDisplayPreferences>('share-updates', {
  operation: 'load-wall-display',
})

export const saveWallDisplayPreferences = (preferences: Partial<WallDisplayPreferences> & Pick<WallDisplayPreferences, 'wallAccumulation'>) =>
  callSocialFunction<WallDisplayPreferences>('share-updates', {
    operation: 'set-wall-display',
    ...preferences,
  })

export const loadOwnerDisplaySettings = () => callSocialFunction<OwnerDisplaySettings>('share-updates', {
  operation: 'load-display-settings',
})

export const saveOwnerDisplaySettings = (expectedDisplayVersion: number, settings: Omit<OwnerDisplaySettings, 'displayVersion'>) =>
  callSocialFunction<OwnerDisplaySettings>('share-updates', {
    operation: 'set-display-settings', expectedDisplayVersion,
    scoreboard: settings.scoreboard,
    wall: settings.wall,
  })

export const createInvitation = (email: string, projectionLevel: ProjectionLevel) =>
  callSocialFunction<{ invitationId: string; projectionLevel: ProjectionLevel; expiresAt: string; url: string }>(
    'invitation-create',
    { email, projectionLevel, expiresInHours: 72 },
  )

export const revokeInvitation = (invitationId: string) => callSocialFunction<{ state: 'revoked' }>(
  'share-updates',
  { operation: 'revoke-invitation', invitationId },
)

export const setOutgoingGrant = (granteeUserId: string, projectionLevel: ProjectionLevel, active = true) =>
  callSocialFunction<{ grant: unknown }>('share-updates', {
    operation: 'set-outgoing-grant', granteeUserId, projectionLevel, active,
  })

export const setIncomingPreference = (ownerId: string, projectionLevel: ProjectionLevel) =>
  callSocialFunction<{ projectionLevel: ProjectionLevel }>('share-updates', {
    operation: 'set-incoming-preference', ownerId, projectionLevel, hidden: false, displayOrder: 0,
  })

export const createWall = (projectionLevel: ProjectionLevel) => callSocialFunction<{
  wallLinkId: string; projectionLevel: ProjectionLevel; url: string; replaced: boolean
}>('share-updates', { operation: 'create-wall', projectionLevel })

export const rotateWall = (previousLinkId: string, projectionLevel: ProjectionLevel) => callSocialFunction<{
  wallLinkId: string; projectionLevel: ProjectionLevel; url: string; replaced: boolean
}>('share-updates', { operation: 'rotate-wall', previousLinkId, projectionLevel })

export const revokeWall = (wallLinkId: string) => callSocialFunction<{ state: 'revoked' }>(
  'share-updates',
  { operation: 'revoke-wall', wallLinkId },
)
